<script lang="ts">
  import Icon from "./Icon.svelte";
  import BlockResult from "./results/BlockResult.svelte";
  import AddressResult from "./results/AddressResult.svelte";
  import TransactionResult from "./results/TransactionResult.svelte";
  import WorkflowResult from "./results/WorkflowResult.svelte";

  interface Props {
    result: {
      type: string;
      query?: string;
      workflow?: any;
      data?: any;
    };
  }

  let { result }: Props = $props();

  // Parse what type of result to show based on query
  let resultType = $derived.by(() => {
    if (result.type === "workflow") return "workflow";

    const query = result.query || "";

    // Check if it's a block query
    if (query.startsWith("/block") || /^[0-9]+$/.test(query)) {
      return "block";
    }

    // Check if it's a transaction hash
    if (query.startsWith("/tx") || (query.startsWith("0x") && query.length === 66)) {
      return "transaction";
    }

    // Check if it's an address or ENS
    if (
      query.startsWith("/address") ||
      query.endsWith(".eth") ||
      (query.startsWith("0x") && query.length === 42)
    ) {
      return "address";
    }

    // Default to search
    return "search";
  });

  // Mock data for demo
  let mockBlockData = {
    number: 21245789,
    hash: "0x7d5a4369c...f8b2e1a",
    timestamp: Date.now() / 1000 - 12,
    miner: "0x1f9090...7c42e",
    gasUsed: "12450000",
    gasLimit: "30000000",
    transactions: 156,
    baseFeePerGas: "15.2 gwei",
  };

  let mockAddressData = {
    address: "0xd8da6bf26964af9d7eed9e03e53415d37aa96045",
    ensName: "vitalik.eth",
    balance: "1,234.56 ETH",
    usdValue: "$4,567,890",
    transactionCount: 1245,
    tokens: [
      { name: "USDC", balance: "50,000", usdValue: "$50,000" },
      { name: "UNI", balance: "10,000", usdValue: "$61,200" },
    ],
    recentActivity: [
      { type: "Sent", description: "Sent 2 ETH to fuzztesting.eth", time: "2h ago" },
      { type: "Swap", description: "Swapped 500 USDC for ETH", time: "1d ago" },
      { type: "Deposit", description: "Deposited 500 USDC into Aave", time: "3d ago" },
    ],
  };

  let mockTxData = {
    hash: "0x2d23db...918c",
    status: "Success",
    from: "0xd8da...6045",
    to: "0x1f98...f984",
    value: "2.5 ETH",
    gasUsed: "21,000",
    gasPrice: "15.2 gwei",
    timestamp: Date.now() / 1000 - 120,
    block: 21245788,
    method: "transfer",
  };
</script>

<div class="results-panel">
  {#if resultType === "block"}
    <BlockResult data={mockBlockData} />
  {:else if resultType === "address"}
    <AddressResult data={mockAddressData} />
  {:else if resultType === "transaction"}
    <TransactionResult data={mockTxData} />
  {:else if resultType === "workflow"}
    <WorkflowResult workflow={result.workflow} />
  {:else}
    <div class="search-result">
      <div class="search-icon">
        <Icon name="search" size={24} />
      </div>
      <p class="search-query">Searching for "{result.query}"...</p>
      <p class="search-hint">
        Try searching for an address, transaction hash, block number, or ENS name
      </p>
    </div>
  {/if}
</div>

<style>
  .results-panel {
    min-height: 300px;
  }

  .search-result {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--space-12);
    text-align: center;
  }

  .search-icon {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-secondary);
    border-radius: var(--radius-full);
    color: var(--text-tertiary);
    margin-bottom: var(--space-4);
  }

  .search-query {
    font-size: 1rem;
    color: var(--text-primary);
    margin-bottom: var(--space-2);
  }

  .search-hint {
    font-size: 0.875rem;
    color: var(--text-tertiary);
  }
</style>
